import { Controller } from "@hotwired/stimulus"

// Live-edit controller for the cook grocery diff modal.
// Buttons MOVE rows between sections client-side (after the server action
// succeeds for ones that touch real data) so the UI matches the new state.
export default class extends Controller {
  static targets = [
    "row", "rowName", "checkbox",
    "pantryRow", "onListRow", "submit", "empty",
    "needToAddSection", "needToAddList", "needToAddCount",
    "onListSection",    "onListList",    "onListCount",
    "pantrySection",    "pantryList",    "pantryCount",
    "ctaCount"
  ]
  static values = { recipeId: Number }

  #pantryListContains(name) {
    if (!this.hasPantryListTarget) return false
    return Array.from(this.pantryListTarget.children).some((child) => child.dataset?.name === name)
  }

  // "I have it" — user is marking a Need-to-add row as already stocked.
  // POST a single name to /pantry/items/bulk_seed and move the row from
  // Need-to-add → In your pantry. No retailer trip.
  markInPantry(event) {
    event.preventDefault()
    const button = event.currentTarget
    const name = button.dataset.name
    const row = button.closest("[data-grocery-selection-target='row']")
    if (!name) return

    button.disabled = true
    this.#seedPantryWith([ name ])

    if (row) {
      row.remove()
      // Uncheck the row's checkbox first (so the form submission doesn't
      // include this ingredient in the grocery-add payload). Removing the
      // node already accomplishes that, but we belt-and-suspenders.
    }
    if (this.hasPantryListTarget && !this.#pantryListContains(name)) {
      this.#prependPantryRow(name, null)
    }
    this.#refresh()
  }

  #seedPantryWith(names) {
    if (!names.length) return
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const body = new URLSearchParams()
    names.forEach((n) => body.append("names[]", n))
    fetch("/pantry/items/bulk_seed", {
      method: "POST",
      headers: {
        "X-CSRF-Token": token,
        "Content-Type": "application/x-www-form-urlencoded",
        "Accept": "text/html"
      },
      credentials: "same-origin",
      keepalive: true,
      body: body.toString()
    }).catch(() => { /* fire-and-forget; user is mid-redirect */ })
  }

  // ─── Need-to-add row actions ────────────────────────────────────────────

  // Checkbox change on a Need-to-add row — visually mute the row when
  // unchecked, restore when re-checked, and update the bottom CTA count.
  // Form submission of unchecked checkboxes is excluded by the browser,
  // so users see "Add 3 checked to grocery list" reflect their choices.
  updateChecked(event) {
    const checkbox = event.currentTarget
    // Need-to-add row → toggle the visual line-through state. Pantry
    // rows skip this (they're "I have it" rows; line-through doesn't
    // make sense). In both cases #refresh updates the submit count.
    const row = checkbox.closest("[data-grocery-selection-target='row']")
    if (row) {
      const nameSpan = row.querySelector("[data-grocery-selection-target='rowName']")
      if (nameSpan) {
        nameSpan.classList.toggle("line-through", !checkbox.checked)
        nameSpan.classList.toggle("opacity-50", !checkbox.checked)
      }
    }
    this.#refresh()
  }

  // Send-to-Grocery click: optimistically move every checked Need-to-add
  // row into the Already-on-grocery-list section so reopening the modal
  // (without a hard reload) reflects the just-submitted state.
  //
  // CRITICAL: defer via queueMicrotask so the form's submit event fires
  // FIRST and collects checkbox values from rows that are still in the
  // DOM. Removing the rows synchronously from the click handler would
  // detach the checkboxes before the submission picks them up, and the
  // server would receive zero ingredient_ids — silent data loss.
  commitChecked() {
    if (!this.hasNeedToAddListTarget) return

    const checkedRows = Array.from(this.needToAddListTarget.querySelectorAll(
      "[data-grocery-selection-target='row']"
    )).filter((row) => {
      const cb = row.querySelector("input[type='checkbox']")
      return cb && cb.checked
    })

    // setTimeout (macrotask) so the move runs AFTER the form's submit
    // task — microtasks run before form data collection completes.
    setTimeout(() => {
      checkedRows.forEach((row) => {
        const name = row.dataset.name
        row.remove()
        if (name) this.#prependOnListRow(name, null)
      })
      this.#refresh()
    }, 0)
  }

  // ─── In-pantry row actions ──────────────────────────────────────────────

  // + Grocery on a pantry row — dismiss pantry item AND add to grocery list.
  async moveToGrocery(event) {
    event.preventDefault()
    const button = event.currentTarget
    const row = button.closest("[data-grocery-selection-target='pantryRow']")
    const name = button.dataset.name
    if (!row || !name) return

    button.disabled = true
    const result = await this.#post(button.dataset.url, null, "POST", { json: true })
    if (!result) return this.#fail(button)

    this.#animateOut(row, "moved")
    setTimeout(() => {
      row.remove()
      this.#prependOnListRow(name, result?.grocery_item_id)
      this.#refresh()
    }, 350)
  }

  // ─── Already-on-grocery row actions ────────────────────────────────────

  // + Pantry on a grocery-list row — POST to pantry, DELETE the grocery
  // item, then move to In-pantry.
  async bought(event) {
    event.preventDefault()
    const button = event.currentTarget
    const row = button.closest("[data-grocery-selection-target='onListRow']")
    const name = button.dataset.name
    if (!row || !name) return

    button.disabled = true
    const result = await this.#post(button.dataset.pantryUrl, { normalized_name: name }, "POST", { json: true })
    if (!result || !result.id) return this.#fail(button)
    await this.#post(button.dataset.groceryUrl, null, "DELETE")

    this.#animateOut(row, "stocked")
    setTimeout(() => {
      row.remove()
      this.#prependPantryRow(name, result)
      this.#refresh()
    }, 350)
  }

  // ─── Helpers ────────────────────────────────────────────────────────────

  async #post(url, params = null, method = "POST", { json = false } = {}) {
    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    const init = {
      method,
      credentials: "same-origin",
      redirect: "follow",
      headers: { "X-CSRF-Token": csrf, "Accept": json ? "application/json" : "text/html" }
    }
    if (params) {
      init.headers["Content-Type"] = "application/x-www-form-urlencoded"
      init.body = new URLSearchParams(params).toString()
    }

    try {
      const response = await fetch(url, init)
      if (!response.ok) return false
      if (!json) return true
      return await response.json().catch(() => true)
    } catch {
      return false
    }
  }

  #fail(button) {
    button.disabled = false
    button.textContent = "Try again"
    button.classList.add("text-red-600")
  }

  #animateOut(row, kind) {
    const span = row.querySelector("span")
    const tones = {
      stocked: ["bg-warm-50",  "dark:bg-warm-900/20",  "transition-colors", "duration-200"],
      moved:   ["bg-warm-100", "dark:bg-warm-900/30",  "transition-colors", "duration-200"],
      skipped: ["bg-red-50",   "dark:bg-red-900/20",   "opacity-60", "line-through", "transition-all", "duration-200"],
      deleted: ["bg-red-50",   "dark:bg-red-900/20",   "opacity-60", "line-through", "transition-all", "duration-200"]
    }
    const labels = {
      stocked: "✓ Moved to inventory",
      moved:   "✓ Moved to grocery list",
      skipped: "Skipped",
      deleted: "Removed"
    }
    tones[kind].forEach((c) => row.classList.add(c))
    if (span && labels[kind]) span.textContent = labels[kind]
  }

  #prependPantryRow(name, item) {
    if (!this.hasPantryListTarget) return
    // moveToGrocery button is only useful when we have a real pantry item id
    // from the server; an optimistically-added row (server upsert still in
    // flight) has no id yet, so we render the row without the button.
    const groceryBtn = item?.id ? `
      <button type="button"
              data-action="click->grocery-selection#moveToGrocery"
              data-url="/pantry/items/${item.id}/mark_out"
              data-name="${this.#attr(name)}"
              aria-label="Move ${this.#attr(this.#titleize(name))} to grocery list"
              class="shrink-0 inline-flex items-center min-h-11 rounded-full bg-warm-100 dark:bg-warm-900/30 text-warm-700 dark:text-warm-300 hover:bg-warm-200 dark:hover:bg-warm-900/50 px-2.5 py-1.5 text-xs font-bold motion-safe:transition-colors motion-safe:active:scale-[0.97] cursor-pointer">
        <svg aria-hidden="true" class="h-3.5 w-3.5 mr-1" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/></svg>
        Grocery
      </button>
    ` : ""
    const li = document.createElement("li")
    li.dataset.grocerySelectionTarget = "pantryRow"
    li.dataset.name = name
    li.className = "pl-3 pr-1 py-1.5 flex items-center gap-2 text-sm bg-warm-100/40 dark:bg-warm-900/10"
    li.innerHTML = `
      <svg aria-hidden="true" class="h-4 w-4 text-warm-500 shrink-0" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m2.25 12 8.954-8.955a1.126 1.126 0 0 1 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75"/></svg>
      <span class="flex-1 font-medium text-gray-700 dark:text-gray-300">${this.#attr(this.#titleize(name))}</span>
      ${groceryBtn}
    `
    this.pantryListTarget.prepend(li)
  }

  #prependOnListRow(name, groceryItemId) {
    if (!this.hasOnListListTarget) return
    const li = document.createElement("li")
    li.dataset.grocerySelectionTarget = "onListRow"
    li.dataset.name = name
    li.className = "pl-3 pr-1 py-1.5 flex items-center gap-2 text-sm bg-warm-100/40 dark:bg-warm-900/10"

    const removeBtn = groceryItemId ? `
      <button type="button"
              data-action="click->grocery-selection#removeFromGrocery"
              data-url="/grocery_list/grocery_items/${groceryItemId}"
              aria-label="Remove ${this.#attr(this.#titleize(name))} from grocery list"
              class="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 motion-safe:transition-colors cursor-pointer">
        <svg aria-hidden="true" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>
    ` : ""

    li.className = "pl-3 pr-2 py-2 flex items-center gap-1.5 text-sm bg-warm-100/40 dark:bg-warm-900/10"
    li.innerHTML = `
      <span class="inline-flex items-center justify-center h-5 w-5 shrink-0 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400" aria-label="On grocery list">
        <svg aria-hidden="true" class="h-3 w-3" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>
      </span>
      <span class="flex-1 min-w-0 font-medium text-gray-700 dark:text-gray-300 truncate">${this.#attr(this.#titleize(name))}</span>
      ${removeBtn}
    `
    this.onListListTarget.prepend(li)
  }

  #titleize(s) {
    return s.replace(/\b\w/g, (c) => c.toUpperCase())
  }

  // HTML-attribute-safe interpolation. Pantry/recipe names are normalized
  // ascii server-side, but defense-in-depth prevents an "; quote→attr-break
  // injection if that ever changes.
  #attr(s) {
    return String(s).replace(/[&"<>]/g, (c) =>
      ({ "&": "&amp;", "\"": "&quot;", "<": "&lt;", ">": "&gt;" })[c]
    )
  }

  #refresh() {
    // needCount used for both the section badge and the "Add N checked..."
    // bottom CTA: it's the count of rows whose checkbox is currently
    // CHECKED, not the total number of rows. Unchecked rows stay visible
    // (struck-through) so users can re-check them; they just don't count
    // toward the bulk submit.
    const needCount = this.hasNeedToAddListTarget
      ? this.needToAddListTarget.querySelectorAll("input[type='checkbox']:checked").length
      : 0
    // Pantry rows expose an opt-in checkbox for "running low — add to
    // grocery list anyway." Counted separately so the section badge
    // still shows total pantry items, not the checked subset, but the
    // bottom CTA's submit count rolls them in.
    const pantryChecked = this.hasPantryListTarget
      ? this.pantryListTarget.querySelectorAll("input[type='checkbox']:checked").length
      : 0
    const onListCount = this.hasOnListListTarget ? this.onListListTarget.children.length : 0
    const pantryCount = this.hasPantryListTarget ? this.pantryListTarget.children.length : 0
    const visibleNeedRows = this.hasNeedToAddListTarget ? this.needToAddListTarget.children.length : 0
    const submitTotal = needCount + pantryChecked

    if (this.hasNeedToAddCountTarget) this.needToAddCountTarget.textContent = needCount
    if (this.hasOnListCountTarget)    this.onListCountTarget.textContent    = onListCount
    if (this.hasPantryCountTarget)    this.pantryCountTarget.textContent    = pantryCount
    if (this.hasCtaCountTarget)       this.ctaCountTarget.textContent       = submitTotal

    // Section is visible if there are ANY rows (checked or not) — users
    // need to see unchecked rows to be able to re-check them. Pantry
    // section is omitted on the recipe → grocery preview modal
    // (show_pantry_section: false), so guard with hasXTarget.
    if (this.hasNeedToAddSectionTarget) this.#toggle(this.needToAddSectionTarget, visibleNeedRows)
    if (this.hasOnListSectionTarget)    this.#toggle(this.onListSectionTarget,    onListCount)
    if (this.hasPantrySectionTarget)    this.#toggle(this.pantrySectionTarget,    pantryCount)

    // Disable bulk submit when nothing (need OR pantry re-up) is checked.
    if (this.hasSubmitTarget) this.submitTarget.disabled = submitTotal === 0

    const total = visibleNeedRows + onListCount + pantryCount
    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", total > 0)
  }

  #toggle(section, count) {
    if (!section) return
    section.classList.toggle("hidden", count === 0)
  }
}
